import type { LocalAudioTrack, TestMicrophoneReturn, TestSpeakerReturn } from '@zoom/videosdk';
import { FC, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line no-duplicate-imports
import {
  Box,
  Button,
  Button as ChakraButton,
  CloseButton,
  Dialog,
  Flex,
  Heading,
  Portal,
  Select,
  Separator,
  Spinner,
  Tabs,
  createListCollection
} from '@chakra-ui/react';
import ZoomVideo from '@zoom/videosdk';
import { Progress } from 'antd';
import classNames from 'classnames';
import { VBType } from '../../context/device-setting-context';
import { PRESET_BACKGROUNDS } from '../../config/virtual-backgrounds';
import useLanguageSelector from '../../hooks/useLanguageSelector';
import useLogger from '../../hooks/useLogger';
import { LocalAudioTrackEx, LocalVideoTrackEx } from '../../index-types';
import { isMobile } from '../../utils/DeviceType';
import type { MediaDevice } from '../video/video-types';
import './preview.scss';
import { useUnmount } from '../../hooks';
type Props = {
  isOpen: boolean;
  isInVBMode: boolean;
  speakerList: MediaDevice[];
  cameraList: MediaDevice[];
  micList: MediaDevice[];
  activeCamera: string;
  activeMicrophone: string;
  activeSpeaker: string;
  activeVirtualBackground: VBType;
  onSwitchCamera: (key: string) => Promise<void>;
  onSwitchSpeaker: (key: string) => void;
  onSwitchMicrophone: (key: string) => void;
  onVirtualBackgroundChange: (item: SelectItem) => Promise<void>;
  onClose: () => void;
  onDisplayMediaSetting: (value: boolean) => void;
};
type SelectItem = {
  label: string;
  value: string;
};
type MediaSetting = 'video' | 'audio';

let localAudioSetting = ZoomVideo.createLocalAudioTrack() as LocalAudioTrackEx;
let localVideoSetting = ZoomVideo.createLocalVideoTrack() as LocalVideoTrackEx;

const Settings: FC<Props> = ({
  isOpen,
  cameraList,
  micList,
  speakerList,
  activeCamera,
  activeMicrophone,
  activeSpeaker,
  activeVirtualBackground,
  onSwitchCamera,
  onSwitchSpeaker,
  onSwitchMicrophone,
  onVirtualBackgroundChange,
  onClose,
  onDisplayMediaSetting
}) => {
  //   const deviceSettingCtx = useDeviceSettingContext();

  const LANG = useLanguageSelector();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const speakerTesterRef = useRef<TestSpeakerReturn>();
  const microphoneTesterRef = useRef<TestMicrophoneReturn>();
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [outputLevel, setOutputLevel] = useState(0);
  const [inputLevel, setInputLevel] = useState(0);
  const [isInVBMode, setIsInVBMode] = useState(false);
  const [microphoneText, setMicrophoneText] = useState('');
  const logger = useLogger();

  const virtualBackgrouds = [
    {
      key: 'none',
      value: LANG.KT_0100_37
    }
  ];
  if (!isMobile()) {
    virtualBackgrouds.push({
      key: 'blur',
      value: LANG.KT_0100_36
    });
    PRESET_BACKGROUNDS.forEach((bg) => {
      virtualBackgrouds.push({ key: bg.imageUrl, value: bg.label });
    });
  }

  const dialogContentProps: Dialog.ContentProps = {
    ref: contentRef
  };
  const dialogCloseProps: Dialog.CloseTriggerProps = {
    asChild: true
  };
  const selectValueTextProps: Select.ValueTextProps = {
    placeholder: ''
  };
  const tabsTriggerProps: { [key in MediaSetting]: Tabs.TriggerProps } = {
    video: {
      value: 'video'
    },
    audio: {
      value: 'audio'
    }
  };
  const tabsContentProps: { [key in MediaSetting]: Tabs.ContentProps } = {
    video: {
      value: 'video'
    },
    audio: {
      value: 'audio'
    }
  };
  const selectCameraListItem: { items: SelectItem[] } = createListCollection({
    items: cameraList?.map((cam) => {
      return {
        label: cam.label,
        value: cam.deviceId
      };
    })
  });
  const selectVirtualbackgroundItem: { items: SelectItem[] } = createListCollection({
    items: virtualBackgrouds.map((vg) => {
      return {
        label: vg.value,
        value: vg.key
      };
    })
  });
  const selectMicrophoneListItem: { items: SelectItem[] } = createListCollection({
    items: micList?.map((mic) => {
      return {
        label: mic.label,
        value: mic.deviceId
      };
    })
  });
  const selectSpeakerListItem: { items: SelectItem[] } = createListCollection({
    items: speakerList?.map((spk) => {
      return {
        label: spk.label,
        value: spk.deviceId
      };
    })
  });

  const startLocalVideo = async () => {
    if (videoRef.current && !localVideoSetting.isVideoStarted) {
      try {
        await localVideoSetting?.start(videoRef.current);

        logger.log('video start');
      } catch (error) {
        logger.error(error);
      }
    }
  };
  const startLocalCanvasVideo = async (imageUrl = '') => {
    if (canvasRef.current && !localVideoSetting.isVideoStarted) {
      try {
        await localVideoSetting.start(canvasRef.current, { imageUrl: imageUrl });
        logger.log('canvas video start');
      } catch (error) {
        logger.error(error);
      }
    }
    setIsInVBMode(true);
  };
  const onSwitchSettingCamera = (key: string) => {
    localVideoSetting.switchCamera(key);
  };
  const onSwitchSettingMic = (key: string) => {
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
    }
    if (isRecordingVoice || isPlayingRecording) {
      microphoneTesterRef.current?.stop();
      setIsRecordingVoice(false);
      setIsPlayingRecording(false);
    }
  };
  const onSwitchSettingSpeaker = (key: string) => {
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
      setOutputLevel(0);
    }
  };
  const onSwitchVirtualBackground = async () => {
    await localVideoSetting.stop();
    if (activeVirtualBackground !== 'none') {
      await startLocalCanvasVideo(activeVirtualBackground);
      setIsInVBMode(true);
    } else {
      await startLocalVideo();
      setIsInVBMode(false);
    }
  };

  const onTestSpeakerClick = () => {
    if (microphoneTesterRef.current) {
      microphoneTesterRef.current.destroy();
      microphoneTesterRef.current = undefined;
      setIsRecordingVoice(false);
      setIsPlayingRecording(false);
    }
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
      setOutputLevel(0);
    } else {
      speakerTesterRef.current = localAudioSetting.testSpeaker({
        speakerId: activeSpeaker,
        onAnalyseFrequency: (value) => {
          setOutputLevel(Math.min(100, value));
        }
      });
      setIsPlayingAudio(true);
    }
  };
  const onTestMicrophoneClick = () => {
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
      setIsPlayingAudio(false);
    }
    if (!isPlayingRecording && !isRecordingVoice) {
      if (microphoneTesterRef.current && microphoneTesterRef.current?.stop) {
        microphoneTesterRef.current.stop();
        microphoneTesterRef.current.destroy();
      }
      microphoneTesterRef.current = localAudioSetting.testMicrophone({
        microphoneId: activeMicrophone,
        speakerId: activeSpeaker,
        recordAndPlay: true,
        onAnalyseFrequency: (value) => {
          setInputLevel(Math.min(100, value));
        },
        onStartRecording: () => {
          setIsRecordingVoice(true);
        },
        onStartPlayRecording: () => {
          setIsRecordingVoice(false);
          setIsPlayingRecording(true);
        },
        onStopPlayRecording: () => {
          setIsPlayingRecording(false);
        }
      });
    } else if (isRecordingVoice) {
      microphoneTesterRef.current?.stopRecording();
      setIsRecordingVoice(false);
    } else if (isPlayingRecording) {
      microphoneTesterRef.current?.stop();
      setIsPlayingRecording(false);
    }
  };
  const handleClose = () => {
    if (localVideoSetting.isVideoStarted) {
      localVideoSetting.stop();
    }
    // テストオーディオ停止
    if (isPlayingAudio) {
      onTestSpeakerClick();
    }
    onClose();
  };

  useEffect(() => {
    onSwitchSettingCamera(activeCamera);
  }, [activeCamera]);
  useEffect(() => {
    onSwitchSettingMic(activeMicrophone);
  }, [activeMicrophone]);
  useEffect(() => {
    onSwitchSettingSpeaker(activeSpeaker);
  }, [activeSpeaker]);
  useEffect(() => {
    if (localVideoSetting.isVideoStarted) {
      onSwitchVirtualBackground();
    }
  }, [activeVirtualBackground]);
  useEffect(() => {
    if (isRecordingVoice) {
      setMicrophoneText('Recording');
    } else if (isPlayingRecording) {
      setMicrophoneText('Playing');
    } else {
      setMicrophoneText('Test Microphone');
    }
  }, [isRecordingVoice, isPlayingRecording]);

  useEffect(() => {
    const wait = () => {
      if (canvasRef.current) {
        if (activeVirtualBackground !== 'none') {
          startLocalCanvasVideo(activeVirtualBackground);
        } else {
          startLocalVideo();
        }
      } else {
        requestAnimationFrame(wait);
      }
    };
    if (isOpen) {
      wait();
    }
  }, [isOpen]);
  useUnmount(() => {
    if (localVideoSetting.isVideoStarted) {
      localVideoSetting.stop();
    }
    if (localAudioSetting.isAudioStarted) {
      localAudioSetting.stop();
    }
  });
  return (
    <>
      <Dialog.Root
        layzyMount
        open={isOpen}
        onOpenChange={(e: any) => {
          onDisplayMediaSetting(e.open);
        }}
        closeOnInteractOutside={true}
      >
        <Portal>
          <Dialog.Positioner>
            <Dialog.Backdrop />
            <Dialog.Content {...dialogContentProps}>
              <Dialog.CloseTrigger {...dialogCloseProps}>
                <CloseButton onClick={handleClose} />
              </Dialog.CloseTrigger>
              <Dialog.Header>
                <Dialog.Title>{LANG.KT_0100_06}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Box margin={'10px'}>
                  <Tabs.Root defaultValue="video">
                    <Tabs.List>
                      <Tabs.Trigger {...tabsTriggerProps['video']}>{LANG.KT_0100_07}</Tabs.Trigger>
                      <Tabs.Trigger {...tabsTriggerProps['audio']}>{LANG.KT_0100_10}</Tabs.Trigger>
                    </Tabs.List>
                    <Tabs.Content {...tabsContentProps['video']}>
                      <Box margin={'15px 0'}>
                        <Box
                          background={'rgba(0, 0, 0, 1)'}
                          borderRadius={'md'}
                          overflow={'hidden'}
                          className="preview-video"
                          position={'relative'}
                          margin={'auto'}
                          maxHeight={'180px'}
                          aspectRatio={isMobile() ? '9 / 16' : '16 / 9'}
                        >
                          <video
                            className={classNames({ 'preview-video-show': !isInVBMode })}
                            muted={true}
                            playsInline
                            ref={videoRef}
                          />
                          <canvas
                            className={classNames({ 'preview-video-show': isInVBMode })}
                            width="1280"
                            height="720"
                            ref={canvasRef}
                          />
                        </Box>

                        <Separator margin={'15px 0'} />
                        <Box textAlign={'start'}>
                          <Select.Root
                            collection={selectCameraListItem}
                            defaultValue={[activeCamera]}
                            onValueChange={(details: { items: SelectItem[]; value: string[] }) => {
                              console.log(details);
                              onSwitchCamera(details.value[0]);
                            }}
                          >
                            <Select.HiddenSelect />
                            <Select.Label>{LANG.KT_0100_08}</Select.Label>
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText {...selectValueTextProps} />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal container={contentRef}>
                              <Select.Positioner>
                                <Select.Content>
                                  {selectCameraListItem.items.map((cam) => {
                                    const props: Select.ItemProps = {
                                      item: cam,
                                      key: cam.value,
                                      value: cam.value
                                    };
                                    return (
                                      <Select.Item {...props}>
                                        {cam.label}
                                        <Select.ItemIndicator />
                                      </Select.Item>
                                    );
                                  })}
                                </Select.Content>
                              </Select.Positioner>
                            </Portal>
                          </Select.Root>
                        </Box>
                        <Box textAlign={'start'}>
                          <Select.Root
                            collection={selectVirtualbackgroundItem}
                            defaultValue={[activeVirtualBackground]}
                            onValueChange={(details: { items: SelectItem[]; value: string[] }) => {
                              onVirtualBackgroundChange(details.items[0]);
                            }}
                          >
                            <Select.HiddenSelect />
                            <Select.Label>{LANG.KT_0100_35}</Select.Label>
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText {...selectValueTextProps} />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal container={contentRef}>
                              <Select.Positioner>
                                <Select.Content>
                                  {selectVirtualbackgroundItem.items.map((vg) => {
                                    const props: Select.ItemProps = {
                                      item: vg,
                                      key: vg.value
                                    };
                                    return (
                                      <Select.Item {...props}>
                                        {vg.label}
                                        <Select.ItemIndicator />
                                      </Select.Item>
                                    );
                                  })}
                                </Select.Content>
                              </Select.Positioner>
                            </Portal>
                          </Select.Root>
                        </Box>
                      </Box>
                    </Tabs.Content>
                    <Tabs.Content {...tabsContentProps['audio']}>
                      <Box margin={'15px 0'}>
                        <Heading size={'md'} margin={'10px'}>
                          {LANG.KT_0100_39}
                        </Heading>
                        <Box textAlign={'start'}>
                          <Select.Root
                            collection={selectMicrophoneListItem}
                            defaultValue={[activeMicrophone]}
                            onValueChange={(details: { items: SelectItem[]; value: string[] }) => {
                              console.log(details);
                              onSwitchMicrophone(details.value[0]);
                            }}
                          >
                            <Select.Label>{LANG.KT_0100_11}</Select.Label>
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText {...selectValueTextProps} />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal container={contentRef}>
                              <Select.Positioner>
                                <Select.Content>
                                  {selectMicrophoneListItem.items.map((mic) => {
                                    const props: Select.ItemProps = {
                                      item: mic,
                                      key: mic.value
                                    };
                                    return (
                                      <Select.Item {...props}>
                                        {mic.label}
                                        <Select.ItemIndicator />
                                      </Select.Item>
                                    );
                                  })}
                                </Select.Content>
                              </Select.Positioner>
                            </Portal>
                          </Select.Root>
                        </Box>
                      </Box>
                      <Box margin={'15px 0'} textAlign={'start'}>
                        <Heading size={'sm'} margin={'10px'}>
                          Microphone Test
                        </Heading>

                        {isMobile() ? (
                          <Flex flexDirection={'column'}>
                            <ChakraButton
                              width={'100%'}
                              onClick={onTestMicrophoneClick}
                              color={'white'}
                              colorPalette={'blue'}
                              whiteSpace={'pre-wrap'}
                            >
                              {microphoneText}
                            </ChakraButton>
                            <Box width={'100%'}>
                              <span className="speaker-label">Input level</span>
                              <Progress percent={inputLevel} showInfo={false} />
                            </Box>
                          </Flex>
                        ) : (
                          <Flex gap={'10px'}>
                            <ChakraButton
                              marginRight={'5px'}
                              width={'30%'}
                              onClick={onTestMicrophoneClick}
                              color={'white'}
                              colorPalette={'blue'}
                              whiteSpace={'pre-wrap'}
                            >
                              {microphoneText}
                            </ChakraButton>
                            <Box width={'100%'}>
                              <span className="speaker-label">Input level</span>
                              <Progress percent={inputLevel} showInfo={false} />
                            </Box>
                          </Flex>
                        )}
                      </Box>
                      <Separator margin={'15px 0'} />
                      <Box margin={'15px 0'}>
                        <Heading size={'md'} margin={'10px'}>
                          {LANG.KT_0100_34}
                        </Heading>
                        <Box textAlign={'start'}>
                          <Select.Root
                            collection={selectSpeakerListItem}
                            defaultValue={[activeSpeaker]}
                            onValueChange={(details: { items: SelectItem[]; value: string[] }) => {
                              console.log(details);
                              onSwitchSpeaker(details.value[0]);
                            }}
                          >
                            <Select.HiddenSelect />
                            <Select.Label>{LANG.KT_0100_38}</Select.Label>
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText {...selectValueTextProps} />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal container={contentRef}>
                              <Select.Positioner>
                                <Select.Content>
                                  {selectSpeakerListItem.items.map((spk) => {
                                    const props: Select.ItemProps = {
                                      item: spk,
                                      key: spk.value
                                    };
                                    return (
                                      <Select.Item {...props}>
                                        {spk.label}
                                        <Select.ItemIndicator />
                                      </Select.Item>
                                    );
                                  })}
                                </Select.Content>
                              </Select.Positioner>
                            </Portal>
                          </Select.Root>
                        </Box>
                        <Box textAlign={'start'}>
                          <Heading size={'sm'} margin={'10px'}>
                            Speaker Test
                          </Heading>

                          {isMobile() ? (
                            <Flex flexDirection={'column'}>
                              <ChakraButton
                                width={'100%'}
                                onClick={onTestSpeakerClick}
                                color={'white'}
                                colorPalette={'blue'}
                                whiteSpace={'pre-wrap'}
                              >
                                {isPlayingAudio ? 'Stop' : 'Test Speaker'}
                              </ChakraButton>
                              <Box width={'100%'}>
                                <span className="speaker-label">Output level</span>
                                <Progress percent={outputLevel} showInfo={false} />
                              </Box>
                            </Flex>
                          ) : (
                            <Flex gap={'10px'}>
                              <ChakraButton
                                marginRight={'5px'}
                                width={'30%'}
                                onClick={onTestSpeakerClick}
                                color={'white'}
                                colorPalette={'blue'}
                                whiteSpace={'pre-wrap'}
                              >
                                {isPlayingAudio ? 'Stop' : 'Test Speaker'}
                              </ChakraButton>
                              <Box width={'100%'}>
                                <span className="speaker-label">Output level</span>
                                <Progress percent={outputLevel} showInfo={false} />
                              </Box>
                            </Flex>
                          )}
                        </Box>
                      </Box>
                    </Tabs.Content>
                  </Tabs.Root>
                </Box>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};
export default Settings;
